import { useNavigate } from 'react-router-dom';
import { Chip, TableCell, TableRow, Tooltip, Box } from '@mui/material';
import { formatDate } from '../../../helpers/formatDate';
import { SettlementShort } from '../../../types';
import { numberToUSDCurrency } from '../../../helpers/numberToUSDCurrency';
import { SETTLEMENT_TYPE_OPTIONS, SettlementStatusMap } from './constants';
import { useHasPermission } from '../../../hooks/useHasPermission';
import { reInitiateCustomerOnboarding } from '../../../api/clientsApi/clientsApi';
import useSnackBar from '../../../hooks/useSnackBar';
import { StatusChip } from '../../../components/StatusChip';
import { DashboardApprovalStatusMap } from '../../FirmBankingView/constants';
import SettlementsDistributionCell from '../../../components/SettlementsDistributionCell';
import OnboardingStatusIcon from '../../../components/OnboardingStatusIcon';
import theme from '../../../theme';

interface SettlementsTableDataRowProps {
  tableData: SettlementShort[];
  caseId?: string;
  type?: string;
}

const SettlementsTableDataRow = ({ tableData, caseId }: SettlementsTableDataRowProps) => {
  const navigate = useNavigate();
  const { setAlert } = useSnackBar();

  const canEdit = useHasPermission('Payments', 'Edit');
  const showUserOnboardingDetails = localStorage.getItem('showUserOnboardingDetails') === 'true';
  const showApprovalFlow = localStorage.getItem('hasSettlementApprovalFlow') === 'true';

  const onTableRowClick = (id: number) => {
    navigate(`/payments/settlement/${id}`);
  };

  const handleResendInvite = (customerId?: number, settlementId?: number) => {
    if (!customerId) {
      setAlert((prev) => ({
        ...prev,
        message: 'Customer id is not available for re-initiating onboarding.',
        type: 'error',
      }));
      return;
    }

    reInitiateCustomerOnboarding(customerId, settlementId)
      .then(() => {
        setAlert((prev) => ({
          ...prev,
          message:
            'The Onboarding process has been re-initiated. Please check your email for more information.',
          type: 'success',
        }));
      })
      .catch((err) => {
        setAlert((prev) => ({
          ...prev,
          message: err.response.data.message,
          type: 'error',
        }));
      });
  };



  const formattedFirmsList =
    tableData &&
    tableData.map((settlement: SettlementShort) => ({
      id: settlement.id,
      rowFields: {
        // name: settlement.case.name,
        status: '',
        referenceId: settlement.case?.referenceId || '-',
        settlementType: Object.values(SETTLEMENT_TYPE_OPTIONS).find(
          (option) => option.value === settlement.settlementType?.toLowerCase()
        )?.label || settlement.settlementType || '-',
        ...(!caseId && {
          plaintiff: `${settlement.client.firstName} ${settlement.client.middleName || ''} ${settlement.client.lastName
            }`,
        }),
        // ...(!caseId && { defendant: settlement.defendant?.name || '-' }),

        // entryDate: formatDate(settlement.createdAt as string),
        updatedAt: formatDate(settlement.updatedAt as string),
        grossSettlement: numberToUSDCurrency(
          settlement.settlementType?.toLowerCase() === SETTLEMENT_TYPE_OPTIONS.standardPip.value
            ? (settlement.settledAmount || 0) + (settlement.pipAmount || 0)
            : settlement.settlementType?.toLowerCase() === SETTLEMENT_TYPE_OPTIONS.standardLienReimbursement.value
              ? (settlement.settledAmount || 0) + (settlement.lienReimbursementAmount || 0)
              : settlement.settledAmount
        ),
        ...(showApprovalFlow && {
          processingStatus: settlement.processingStatus,
        }),
        ...(showUserOnboardingDetails && {
          onboardingStatus: settlement.onboardingStatus,
        }),
        distribution: '',
        ...(showUserOnboardingDetails && {
          actions: (
            <Chip
              label="Resend Invite"
              variant="outlined"
              disabled={settlement.resendOnboardingInvite !== true || !canEdit}
              onClick={() => handleResendInvite(settlement.customerId, settlement.id)}
              sx={{
                height: '25px',
                borderRadius: '12px',
                border: '1px solid #38BDF8',
                background: '#38BDF814',
                width: 'fit-content',
                '& .MuiChip-label': {
                  fontFamily: 'Inter',
                  fontSize: '13px',
                  fontWeight: 600,
                  color: '#38BDF8',
                  padding: '4px 12px',
                },
                '&.Mui-disabled': {
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  background: 'rgba(255, 255, 255, 0.08)',
                },
                '&.Mui-disabled .MuiChip-label': {
                  color: 'rgba(255, 255, 255, 0.35)',
                },
              }}
            />
          ),
        }),
      },
    }));

  return (
    <>
      {formattedFirmsList.map((tableItem, idx) => {
        return (
          <TableRow key={idx} onClick={() => onTableRowClick(tableItem.id)}>
            {Object.entries(tableItem.rowFields).map(([key, value], idx1) => {
              return (
                <TableCell key={idx1}>
                  {key === 'onboardingStatus' ? (
                    <OnboardingStatusIcon status={String(value)} />
                  ) : key === 'distribution' ? (
                    <SettlementsDistributionCell
                      settlementId={tableItem.id}
                      sequences={tableData[idx]?.sequences}
                    />
                  ) : key === 'status' ? (
                    <div style={{ display: 'flex', gap: '4px' }}>
                      {tableData[idx]?.sequences?.map((seq, seqIdx) => {
                        const statusKey = seq.status as keyof typeof SettlementStatusMap;
                        const statusInfo = SettlementStatusMap[statusKey];
                        return (
                          <Tooltip
                            key={seqIdx}
                            arrow
                            title={`Wave ${seq.sequenceNumber} - ${statusInfo?.label || seq.status}`}
                            slotProps={{
                              tooltip: {
                                sx: (theme) => ({
                                  backgroundColor: theme.palette.common.white,
                                  color: theme.palette.primary.light,
                                  borderRadius: '5px',
                                  boxShadow: '3px',
                                  fontFamily: 'Inter',
                                  fontSize: '13px',
                                  fontWeight: 500,
                                  padding: '5px',
                                  maxWidth: '140px',
                                }),
                              },
                              arrow: {
                                sx: (theme) => ({
                                  color: theme.palette.common.white,
                                  '&:before': {
                                    boxShadow: 3,
                                  },
                                }),
                              },
                            }}
                          >
                            <div
                              key={seqIdx}
                              style={{
                                width: 10,
                                height: 10,
                                borderRadius: '3px',
                                background: statusInfo?.color || theme.palette.text.disabled,
                                border: `1px solid ${theme.palette.secondary.main}`,
                                cursor: 'pointer',
                              }}
                            />
                          </Tooltip>
                        );
                      })}
                    </div>
                  ) : key === 'settlementType' ? (
                    value !== '-' ? (
                      <Tooltip
                        arrow
                        title={String(value)}
                        componentsProps={{
                          tooltip: {
                            sx: (theme) => ({
                              backgroundColor: theme.palette.common.white,
                              color: theme.palette.primary.light,
                              borderRadius: '5px',
                              boxShadow: '3px',
                              fontFamily: 'Inter',
                              fontSize: '13px',
                              fontWeight: 500,
                              padding: '5px',
                              maxWidth: '250px',
                            }),
                          },
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', padding: '0px 4px', '& .MuiChip-root': { height: '22px' }, '& .MuiChip-label': { fontSize: '12px !important' } }}>
                          <StatusChip
                            label={String(value)}
                            color={
                              [SETTLEMENT_TYPE_OPTIONS.standardPip.value, SETTLEMENT_TYPE_OPTIONS.standardLienReimbursement.value]
                                .includes(tableData[idx]?.settlementType?.toLowerCase() ?? '')
                                ? theme.palette.success.main
                                : theme.palette.text.disabled
                            }
                          />
                        </Box>
                      </Tooltip>
                    ) : (
                      value
                    )
                  ) : (showApprovalFlow && key === 'processingStatus') ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', padding: '0px 4px', '& .MuiChip-root': { height: '22px' }, '& .MuiChip-label': { fontSize: '12px !important' } }}>
                      <StatusChip
                        label={DashboardApprovalStatusMap[value as keyof typeof DashboardApprovalStatusMap]?.label || String(value)}
                        color={DashboardApprovalStatusMap[value as keyof typeof DashboardApprovalStatusMap]?.color || 'default'}
                      />
                    </Box>
                  ) : (
                    value
                  )}
                </TableCell>
              );

            })}
          </TableRow>
        );
      })}
    </>
  );
};

export default SettlementsTableDataRow;